import { useEffect, useCallback } from 'react';
import { useAudioStore } from './useAudioStore';
import { EQ_BANDS } from '@/constants/equalizer';
import { isMobileApp, mobileBridge } from '@/utils/mobileBridge';
import type { RadioStation, TrackInfo } from '@/types';

/** Modulski refs – ostaju aktivni pri navigaciji (unmount komponenti) */
const audioRef = { current: null as HTMLAudioElement | null };
const ctxRef = { current: null as AudioContext | null };
const sourceRef = { current: null as MediaElementAudioSourceNode | null };
const filtersRef = { current: [] as BiquadFilterNode[] };
const gainRef = { current: null as GainNode | null };              // user volume (0..1)
const compressorRef = { current: null as DynamicsCompressorNode | null }; // clipping protection / loudness norm
const masterGainRef = { current: null as GainNode | null };        // fixed +6 dB master boost

/** Master output boost — kompenzira HTML5→WebAudio drop + radio LUFS + Bluetooth A2DP loss */
const MASTER_BOOST_DB = 6;
const MASTER_BOOST_LINEAR = Math.pow(10, MASTER_BOOST_DB / 20); // ≈ 1.995

/** Volume slider ramp — sprečava klikove pri brzom povlačenju */
const VOLUME_RAMP_SECONDS = 0.03;

/* ─────────────────────────────────────────────────────────────
 * navigator.mediaSession integracija (FIX-D)
 *
 * Eksplicitno postavlja MediaMetadata + setActionHandler za HTML5 audio
 * element koji svira live stream. U Chrome za Android i Android System
 * WebView 100+ ovo omogućava lock-screen / notification kontrole sa
 * pravilnim naslovom stanice/pjesme + ikonom + radom play/pause/stop.
 *
 * Sav kod je gracefully no-op kad `navigator.mediaSession` ne postoji
 * (stari browseri, neki desktop WebView-i).
 * ─────────────────────────────────────────────────────────────── */

function isMediaSessionSupported(): boolean {
  return typeof navigator !== 'undefined' && 'mediaSession' in navigator;
}

function buildMetadata(station: RadioStation, track: TrackInfo | null): MediaMetadata {
  const artworkUrl = (track?.artwork && track.artwork.length > 0) ? track.artwork : station.artwork;
  const artwork = artworkUrl
    ? [
        { src: artworkUrl, sizes: '96x96', type: 'image/png' },
        { src: artworkUrl, sizes: '192x192', type: 'image/png' },
        { src: artworkUrl, sizes: '512x512', type: 'image/png' },
      ]
    : [];

  if (track && (track.artist || track.title)) {
    return new MediaMetadata({
      title: track.title || station.name,
      artist: track.artist || station.name,
      album: station.name,
      artwork,
    });
  }

  return new MediaMetadata({
    title: station.name,
    artist: 'SUTON Radio',
    album: station.genre || '',
    artwork,
  });
}

function updateMediaSessionMetadata(station: RadioStation | null, track: TrackInfo | null): void {
  if (!isMediaSessionSupported() || !station) return;
  try {
    navigator.mediaSession.metadata = buildMetadata(station, track);
  } catch (e) {
    console.warn('[mediaSession] metadata update failed:', e);
  }
}

function setMediaSessionPlaybackState(state: MediaSessionPlaybackState): void {
  if (!isMediaSessionSupported()) return;
  try {
    navigator.mediaSession.playbackState = state;
  } catch (e) {
    console.warn('[mediaSession] playbackState set failed:', e);
  }
}

function clearMediaSession(): void {
  if (!isMediaSessionSupported()) return;
  try {
    navigator.mediaSession.metadata = null;
    navigator.mediaSession.playbackState = 'none';
  } catch {
    /* noop */
  }
}

function setupMediaSessionHandlers(
  audio: HTMLAudioElement,
  onStop: () => void
): void {
  if (!isMediaSessionSupported()) return;
  try {
    navigator.mediaSession.setActionHandler('play', () => {
      audio.play().then(() => {
        setMediaSessionPlaybackState('playing');
      }).catch(() => {
        /* noop — pozivalac (togglePlayPause) ima fallback */
      });
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      audio.pause();
      setMediaSessionPlaybackState('paused');
    });
    navigator.mediaSession.setActionHandler('stop', () => {
      onStop();
      setMediaSessionPlaybackState('none');
    });
    // previoustrack/nexttrack — radio nije playlist; ostavljamo neregistrovano
    // (sistem tada neće prikazivati prev/next dugmad)
  } catch (e) {
    console.warn('[mediaSession] setActionHandler failed:', e);
  }
}

export const useAudioPlayer = () => {
  const {
    isPlaying,
    volume,
    currentStation,
    currentTrack,
    eqBands,
    setPlaying,
    setBuffering,
    setError,
    sleepTimer,
    decrementTimer,
    clearSleepTimer,
  } = useAudioStore();

  // Selektor za normalize toggle (settings live u istom store-u)
  const normalizeVolume = useAudioStore((s) => s.settings.normalizeVolume);

  const initAudioContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    ctxRef.current = new Ctx();
    return ctxRef.current;
  }, []);

  const setupAudioGraph = useCallback(
    (audio: HTMLAudioElement) => {
      const ctx = initAudioContext();
      if (ctx.state === 'suspended') ctx.resume();

      // 1) Source iz HTML5 <audio>
      const source = ctx.createMediaElementSource(audio);
      sourceRef.current = source;

      // 2) Equalizer chain (5x BiquadFilter peaking)
      const filters: BiquadFilterNode[] = [];
      let lastNode: AudioNode = source;
      EQ_BANDS.forEach((band, i) => {
        const filter = ctx.createBiquadFilter();
        filter.type = 'peaking';
        filter.frequency.value = band.freq;
        filter.Q.value = 1;
        filter.gain.value = eqBands[i] ?? 0;
        filters.push(filter);
        lastNode.connect(filter);
        lastNode = filter;
      });
      filtersRef.current = filters;

      // 3) User volume gain (slider 0..1)
      const userGain = ctx.createGain();
      userGain.gain.value = volume;
      gainRef.current = userGain;
      lastNode.connect(userGain);

      // 4) DynamicsCompressor — uvijek aktivan, parametri se mijenjaju normalizeVolume toggle-om
      //    Default ovdje = "transparent protection" (OFF state)
      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = normalizeVolume ? -32 : -24;
      compressor.knee.value = normalizeVolume ? 20 : 30;
      compressor.ratio.value = normalizeVolume ? 8 : 4;
      compressor.attack.value = normalizeVolume ? 0.005 : 0.003;
      compressor.release.value = normalizeVolume ? 0.1 : 0.25;
      compressorRef.current = compressor;
      userGain.connect(compressor);

      // 5) Master output gain — fiksni +6 dB boost
      const masterGain = ctx.createGain();
      masterGain.gain.value = MASTER_BOOST_LINEAR;
      masterGainRef.current = masterGain;
      compressor.connect(masterGain);

      // 6) Destination
      masterGain.connect(ctx.destination);
    },
    [initAudioContext, volume, eqBands, normalizeVolume]
  );

  // Volume sync iz Zustand → GainNode (sa rampom protiv klikova)
  useEffect(() => {
    if (isMobileApp()) {
      mobileBridge.volume(volume);
      return;
    }
    if (!gainRef.current || !ctxRef.current) return;
    const ctx = ctxRef.current;
    const target = Math.max(0, Math.min(1, volume));
    try {
      gainRef.current.gain.cancelScheduledValues(ctx.currentTime);
      gainRef.current.gain.setValueAtTime(gainRef.current.gain.value, ctx.currentTime);
      gainRef.current.gain.linearRampToValueAtTime(target, ctx.currentTime + VOLUME_RAMP_SECONDS);
    } catch {
      gainRef.current.gain.value = target;
    }
  }, [volume]);

  useEffect(() => {
    // R6: u mobile WebView mode-u EQ ide u native Android Equalizer
    // (suton-audio-eq attached na RNTP ExoPlayer audio session). PWA mode
    // koristi BiquadFilter graf na Web Audio API-ju kao i prije.
    if (isMobileApp()) {
      mobileBridge.equalizer(eqBands.map((g) => g ?? 0));
      return;
    }
    filtersRef.current.forEach((filter, i) => {
      if (filter) filter.gain.value = eqBands[i] ?? 0;
    });
  }, [eqBands]);

  // === NORMALIZE VOLUME — toggle iz Settings ===
  // OFF: blagi compressor (samo sprečava clipping)  -24 dB / 4:1 / knee 30
  // ON:  agresivniji compressor (izjednačava glasnoću) -32 dB / 8:1 / knee 20
  useEffect(() => {
    if (isMobileApp()) return;
    const compressor = compressorRef.current;
    const ctx = ctxRef.current;
    if (!compressor || !ctx) return;

    const t = ctx.currentTime;
    if (normalizeVolume) {
      compressor.threshold.setValueAtTime(-32, t);
      compressor.knee.setValueAtTime(20, t);
      compressor.ratio.setValueAtTime(8, t);
      compressor.attack.setValueAtTime(0.005, t);
      compressor.release.setValueAtTime(0.1, t);
      console.log('[Audio] Volume normalization: ON (aggressive compressor)');
    } else {
      compressor.threshold.setValueAtTime(-24, t);
      compressor.knee.setValueAtTime(30, t);
      compressor.ratio.setValueAtTime(4, t);
      compressor.attack.setValueAtTime(0.003, t);
      compressor.release.setValueAtTime(0.25, t);
      console.log('[Audio] Volume normalization: OFF (transparent protection)');
    }
  }, [normalizeVolume]);

  const pause = useCallback(() => {
    if (isMobileApp()) {
      mobileBridge.pause();
      setPlaying(false);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      // Live stream: zatvori konekciju (ne trosi data u pauzi). Nastavak = playStation u togglePlayPause.
      audioRef.current.src = '';
      setPlaying(false);
      setMediaSessionPlaybackState('paused');
    }
  }, [setPlaying]);

  useEffect(() => {
    if (!sleepTimer.isActive) return;
    const interval = setInterval(() => {
      decrementTimer();
      if (useAudioStore.getState().sleepTimer.remainingSeconds <= 1) {
        pause();
        clearSleepTimer();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sleepTimer.isActive, pause, decrementTimer, clearSleepTimer]);

  // FIX-D: Sync mediaSession metadata kad currentTrack ili currentStation se promijeni
  // (npr. nova pjesma stigne preko AzuraCast/Zeno polling-a -> lock screen prikazuje pravi naslov).
  useEffect(() => {
    if (isMobileApp()) return;
    if (!currentStation) return;
    updateMediaSessionMetadata(currentStation, currentTrack);
  }, [currentStation, currentTrack]);

  /** Auto-resume kad korisnik vrati app – samo ako je Auto-play uključen u postavkama */
  useEffect(() => {
    if (isMobileApp()) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const { currentStation, isPlaying, settings } = useAudioStore.getState();
      if (!settings.autoPlay || !currentStation || isPlaying) return;
      const audio = audioRef.current;
      if (audio?.src) {
        audio.play().then(() => setPlaying(true)).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [setPlaying]);

  const playStation = useCallback(
    (station: RadioStation) => {
      // MOBILE WebView: native sloj preuzima audio. Ne pravimo HTML audio engine,
      // samo postavi store state da UI prikaže novu stanicu i pošalji bridge poruku.
      if (isMobileApp()) {
        setError(null);
        setBuffering(false);
        useAudioStore.getState().setCurrentStation(station);
        const ct = useAudioStore.getState().currentTrack;
        mobileBridge.play({
          stationId: station.id,
          stationName: station.name,
          streamUrl: station.streamUrl,
          artwork: station.artwork,
          color: station.color,
          genre: station.genre,
          currentTrack: ct
            ? { artist: ct.artist, title: ct.title, artwork: ct.artwork }
            : undefined,
        });
        setPlaying(true);
        return;
      }

      try {
        setBuffering(true);
        setError(null);

        if (sourceRef.current) {
          try { sourceRef.current.disconnect(); } catch { /* noop */ }
          sourceRef.current = null;
        }
        filtersRef.current.forEach((f) => {
          try { f.disconnect(); } catch { /* noop */ }
        });
        filtersRef.current = [];
        if (gainRef.current) {
          try { gainRef.current.disconnect(); } catch { /* noop */ }
          gainRef.current = null;
        }
        if (compressorRef.current) {
          try { compressorRef.current.disconnect(); } catch { /* noop */ }
          compressorRef.current = null;
        }
        if (masterGainRef.current) {
          try { masterGainRef.current.disconnect(); } catch { /* noop */ }
          masterGainRef.current = null;
        }
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.removeAttribute('src');
          audioRef.current.load();
          audioRef.current = null;
        }
        if (ctxRef.current?.state === 'suspended') {
          void ctxRef.current.resume();
        }

        const audio = new Audio();
        audio.crossOrigin = 'anonymous';
        audio.preload = 'none';
        audio.src = station.streamUrl;

        audio.oncanplaythrough = () => {
          setBuffering(false);
          setPlaying(true);
        };
        audio.onplay = () => {
          setBuffering(false);
          setPlaying(true);
        };
        audio.onpause = () => setPlaying(false);
        audio.onerror = () => {
          setError('Greška pri reprodukciji');
          setBuffering(false);
          setPlaying(false);
        };

        audioRef.current = audio;
        setupAudioGraph(audio);
        useAudioStore.getState().setCurrentStation(station);

        audio.play().then(() => {
          setPlaying(true);
          setBuffering(false);
          // FIX-D: registruj system media kontrole + initial metadata
          setupMediaSessionHandlers(audio, () => {
            audio.pause();
            audio.src = '';
            useAudioStore.getState().setPlaying(false);
          });
          updateMediaSessionMetadata(station, useAudioStore.getState().currentTrack);
          setMediaSessionPlaybackState('playing');
        }).catch(() => {
          setBuffering(false);
          setPlaying(false);
        });
      } catch (err) {
        console.error(err);
        setError('Greška pri reprodukciji. Provjerite konekciju.');
        setPlaying(false);
        setBuffering(false);
      }
    },
    [setBuffering, setError, setPlaying, setupAudioGraph]
  );

  const play = useCallback(() => {
    if (isMobileApp()) {
      const st = useAudioStore.getState().currentStation;
      if (st) playStation(st);
      return;
    }
    if (!audioRef.current) return;
    audioRef.current
      .play()
      .then(() => {
        setPlaying(true);
        setMediaSessionPlaybackState('playing');
      })
      .catch(() => {
        // play() failuje kad src nije validan - fallback na playStation
        const st = useAudioStore.getState().currentStation;
        if (st) playStation(st);
      });
  }, [setPlaying, playStation]);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
      return;
    }
    if (!currentStation) return;
    // Ako audio nema validan src ni currentSrc, kreiraj novi stream preko playStation.
    const a = audioRef.current;
    if (!a || !a.src || !a.currentSrc) {
      playStation(currentStation);
      return;
    }
    play();
  }, [isPlaying, currentStation, play, pause, playStation]);

  const setPlayerVolume = useCallback((newVolume: number) => {
    const target = Math.max(0, Math.min(1, newVolume));
    useAudioStore.getState().setVolume(target);
    if (isMobileApp()) {
      mobileBridge.volume(target);
      return;
    }
    if (gainRef.current && ctxRef.current) {
      const ctx = ctxRef.current;
      try {
        gainRef.current.gain.cancelScheduledValues(ctx.currentTime);
        gainRef.current.gain.setValueAtTime(gainRef.current.gain.value, ctx.currentTime);
        gainRef.current.gain.linearRampToValueAtTime(target, ctx.currentTime + VOLUME_RAMP_SECONDS);
      } catch {
        gainRef.current.gain.value = target;
      }
    }
  }, []);

  const playPrevStation = useCallback(
    (streams: RadioStation[]) => {
      const station = useAudioStore.getState().currentStation;
      if (!station || streams.length === 0) return;
      const idx = streams.findIndex((s) => s.id === station.id);
      const prevIdx = idx <= 0 ? streams.length - 1 : idx - 1;
      playStation(streams[prevIdx]);
    },
    [playStation]
  );

  const playNextStation = useCallback(
    (streams: RadioStation[]) => {
      const station = useAudioStore.getState().currentStation;
      if (!station || streams.length === 0) return;
      const idx = streams.findIndex((s) => s.id === station.id);
      const nextIdx = idx >= streams.length - 1 ? 0 : idx + 1;
      playStation(streams[nextIdx]);
    },
    [playStation]
  );

  const stop = useCallback(() => {
    if (isMobileApp()) {
      mobileBridge.stop();
      setPlaying(false);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      setPlaying(false);
      clearMediaSession();
    }
  }, [setPlaying]);

  return {
    playStation,
    play,
    pause,
    stop,
    togglePlayPause,
    setVolume: setPlayerVolume,
    playPrevStation,
    playNextStation,
  };
};
